import React, { lazy, Suspense } from "react";
import { Switch, Route, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import errorReporter from "../utility/errorReporter";
import ErrorFallBack from "./ErrorFallBack/ErrorFallBack";
import BaseProtectedRoute from "../utility/ProtectedRoute";

import Drawer from "../components/Navigation/SideDrawer/SideDrawer";

import SignIn from "../components/SignIn/SignIn";
import { useAuth } from "../../providers/AuthContext";
import { CustomerProvider } from "../../providers/CustomerContext";
import { ProductProvider } from "../../providers/ProductContext";
import { SettingsProvider } from "../../providers/SettingsProvider";
import { CouponProvider } from "../../providers/CouponsProvider";
import { UserProvider } from "../../providers/UserContext";
import { TimesheetTypeProvider } from "../../providers/TimesheetTypeContext";
import Meta from "../../providers/Meta";
import { UnitProvider } from "../../providers/UnitContext";
import { UnitDescriptionProvider } from "../../providers/UnitDescriptionContext";

import { makeStyles, StylesProvider, CircularProgress } from "@material-ui/core";
import _ from "lodash";
import { SalesEquipmentProvider } from "../../providers/SalesEquipmentContext";
import { FormProvider } from "../../providers/DocumentContext";
import { useHRSettings } from "../../providers/HRSettingsProvider";

// --- Route components: lazy-loaded so each page is its own webpack chunk ---
// Keeps the initial dashboard bundle small; each page downloads on first visit.
const AccessibilitySettings = lazy(() => import(/* webpackChunkName: "AccessibilitySettings" */ "./Shop/ShopSettings/AccessibilitySettings"));
const ApprovalHome = lazy(() => import(/* webpackChunkName: "ApprovalHome" */ "./Approval/Approval"));
const AppSettings = lazy(() => import(/* webpackChunkName: "AppSettings" */ "./Settings/AppSettings"));
const BugReportsHome = lazy(() => import(/* webpackChunkName: "BugReportsHome" */ "./BugReports/BugReportsHome"));
const BusinessSettings = lazy(() => import(/* webpackChunkName: "BusinessSettings" */ "./Shop/ShopSettings/BusinessSettings"));
const CareerSettings = lazy(() => import(/* webpackChunkName: "CareerSettings" */ "./Shop/ShopSettings/CareerSettings"));
const CategoryHome = lazy(() => import(/* webpackChunkName: "CategoryHome" */ "./Categories/CategoryHome"));
const ChangelogHome = lazy(() => import(/* webpackChunkName: "ChangelogHome" */ "./Changelog/ChangelogHome"));
const Checklist = lazy(() => import(/* webpackChunkName: "Checklist" */ "./Reporting/ChecklistReport/Checklist"));
const Checklist2 = lazy(() => import(/* webpackChunkName: "Checklist2" */ "./Checklists/Checklist"));
const ChecklistAllPrint = lazy(() => import(/* webpackChunkName: "ChecklistAllPrint" */ "./Reporting/ChecklistReport/ChecklistAllPrint"));
const ChecklistHome = lazy(() => import(/* webpackChunkName: "ChecklistHome" */ "./Checklists/ChecklistHome"));
const ChecklistPrint = lazy(() => import(/* webpackChunkName: "ChecklistPrint" */ "./Checklists/ChecklistPrint"));
const ChecklistReportPrint = lazy(() => import(/* webpackChunkName: "ChecklistReportPrint" */ "./Reporting/ChecklistReport/ChecklistReportPrint"));
const ChecklistTablePrint = lazy(() => import(/* webpackChunkName: "ChecklistTablePrint" */ "./Reporting/ChecklistReport/ChecklistTablePrint"));
const Conversations = lazy(() => import(/* webpackChunkName: "Conversations" */ "./Shop/Order/Conversations"));
const Coupon = lazy(() => import(/* webpackChunkName: "Coupon" */ "./Coupons/Coupon"));
const CouponHome = lazy(() => import(/* webpackChunkName: "CouponHome" */ "./Coupons/CouponHome"));
const Covid = lazy(() => import(/* webpackChunkName: "Covid" */ "./Reporting/CovidReport/Covid"));
const Customer = lazy(() => import(/* webpackChunkName: "Customer" */ "./Customers/Customer"));
const CustomerHome = lazy(() => import(/* webpackChunkName: "CustomerHome" */ "./Customers/CustomerHome"));
const CustomerNew = lazy(() => import(/* webpackChunkName: "CustomerNew" */ "./Customers/CustomerNew"));
const CustomerPrint = lazy(() => import(/* webpackChunkName: "CustomerPrint" */ "../containers/Reporting/CustomerReport/CustomerPrint"));
const CustomerRates = lazy(() => import(/* webpackChunkName: "CustomerRates" */ "./Customers/CustomerRates"));
const CustomerReport = lazy(() => import(/* webpackChunkName: "CustomerReport" */ "./Customers/CustomerReport"));
const CustomerTablePrint = lazy(() => import(/* webpackChunkName: "CustomerTablePrint" */ "../containers/Customers/CustomerTablePrint"));
const Defect = lazy(() => import(/* webpackChunkName: "Defect" */ "./Reporting/DefectReports/DefectReports"));
const DefectSettings = lazy(() => import(/* webpackChunkName: "DefectSettings" */ "./EquipmentInventory/DefectSettings"));
const Delivery = lazy(() => import(/* webpackChunkName: "Delivery" */ "./Reporting/DeliveryReport/Delivery"));
const Document = lazy(() => import(/* webpackChunkName: "Document" */ "./Reporting/DocumentsReport/Document"));
const EmployeeMemo = lazy(() => import(/* webpackChunkName: "EmployeeMemo" */ "./Users/Memos/EmployeeMemo"));
const EmployeeNotif = lazy(() => import(/* webpackChunkName: "EmployeeNotif" */ "./Users/Notifs/EmployeeNotif"));
const Equipment = lazy(() => import(/* webpackChunkName: "Equipment" */ "./EquipmentInventory/Equipment"));
const EquipmentHome = lazy(() => import(/* webpackChunkName: "EquipmentHome" */ "./EquipmentInventory/EquipmentHome"));
const EquipmentIfta = lazy(() => import(/* webpackChunkName: "EquipmentIfta" */ "./EquipmentIfta/EquipmentIfta"));
const EquipmentIftaHome = lazy(() => import(/* webpackChunkName: "EquipmentIftaHome" */ "./EquipmentIfta/EquipmentIftaHome"));
const EquipmentIftaPrint = lazy(() => import(/* webpackChunkName: "EquipmentIftaPrint" */ "./EquipmentIfta/EquipmentIftaPrint"));
const EquipmentNew = lazy(() => import(/* webpackChunkName: "EquipmentNew" */ "./EquipmentInventory/EquipmentNew"));
const EquipmentPrint = lazy(() => import(/* webpackChunkName: "EquipmentPrint" */ "./EquipmentInventory/EquipmentPrint"));
const EquipmentReport = lazy(() => import(/* webpackChunkName: "EquipmentReport" */ "./EquipmentInventory/EquipmentReport"));
const Estimates = lazy(() => import(/* webpackChunkName: "Estimates" */ "./Estimates/Estimates"));
const EstimatesHome = lazy(() => import(/* webpackChunkName: "EstimatesHome" */ "./Estimates/EstimatesHome"));
const Form = lazy(() => import(/* webpackChunkName: "Form" */ "./Forms/Form"));
const FormHome = lazy(() => import(/* webpackChunkName: "FormHome" */ "./Forms/FormHome"));
const FormNew = lazy(() => import(/* webpackChunkName: "FormNew" */ "./Forms/FormNew"));
const FormReport = lazy(() => import(/* webpackChunkName: "FormReport" */ "./Forms/FormReport"));
const GiftCard = lazy(() => import(/* webpackChunkName: "GiftCard" */ "./GiftCards/GiftCard"));
const GiftCardsHome = lazy(() => import(/* webpackChunkName: "GiftCardsHome" */ "./GiftCards/GiftCardsHome"));
const GiftCardsNew = lazy(() => import(/* webpackChunkName: "GiftCardsNew" */ "./GiftCards/GiftCardsNew"));
const GrainOrders = lazy(() => import(/* webpackChunkName: "GrainOrders" */ "./Grain/GrainOrders"));
const GrainOrdersHome = lazy(() => import(/* webpackChunkName: "GrainOrdersHome" */ "./Grain/GrainOrdersHome"));
const GrainOrdersNew = lazy(() => import(/* webpackChunkName: "GrainOrdersNew" */ "./Grain/GrainOrdersNew"));
const GrainPrint = lazy(() => import(/* webpackChunkName: "GrainPrint" */ "./Grain/GrainPrint"));
const Home = lazy(() => import(/* webpackChunkName: "Home" */ "./Home/Home"));
const HR = lazy(() => import(/* webpackChunkName: "HR" */ "./HumanResources/HR"));
const HRHome = lazy(() => import(/* webpackChunkName: "HRHome" */ "./HumanResources/HRHome"));
const HRNew = lazy(() => import(/* webpackChunkName: "HRNew" */ "./HumanResources/HRNew"));
const HRSettings = lazy(() => import(/* webpackChunkName: "HRSettings" */ "./HumanResources/HRSettings"));
const Invoice = lazy(() => import(/* webpackChunkName: "Invoice" */ "./Invoice/Invoice"));
const InvoiceHome = lazy(() => import(/* webpackChunkName: "InvoiceHome" */ "./Invoice/InvoiceHome"));
const InvoiceNew = lazy(() => import(/* webpackChunkName: "InvoiceNew" */ "./Invoice/InvoiceNew"));
const InvoicePrint = lazy(() => import(/* webpackChunkName: "InvoicePrint" */ "./Invoice/InvoicePrint"));
const Job = lazy(() => import(/* webpackChunkName: "Job" */ "./Reporting/JobReport/Job"));
const JobTablePrint = lazy(() => import(/* webpackChunkName: "JobTablePrint" */ "../containers/Reporting/JobReport/JobTablePrint"));
const MSDS = lazy(() => import(/* webpackChunkName: "MSDS" */ "./MSDS/MSDS"));
const MSDSHome = lazy(() => import(/* webpackChunkName: "MSDSHome" */ "./MSDS/MSDSHome"));
const MSDSNew = lazy(() => import(/* webpackChunkName: "MSDSNew" */ "./MSDS/MSDSNew"));
const NewDocReport = lazy(() => import(/* webpackChunkName: "NewDocReport" */ "../components/PDFs/NewDocReport"));
const Order = lazy(() => import(/* webpackChunkName: "Order" */ "../containers/Shop/Order/Order"));
const OrderNew = lazy(() => import(/* webpackChunkName: "OrderNew" */ "./Shop/Order/OrderNew"));
const OrderNewSquare = lazy(() => import(/* webpackChunkName: "OrderNewSquare" */ "./Shop/Order/OrderNewSquare"));
const OrderPrint = lazy(() => import(/* webpackChunkName: "OrderPrint" */ "../containers/Reporting/OrderReport/OrderPrint"));
const Orders = lazy(() => import(/* webpackChunkName: "Orders" */ "../containers/Shop/Orders/Orders"));
const OrderSelectedPrint = lazy(() => import(/* webpackChunkName: "OrderSelectedPrint" */ "./Reporting/OrderReport/OrderSelectedPrint"));
const Payroll = lazy(() => import(/* webpackChunkName: "Payroll" */ "./Payroll/Payroll"));
const PayrollPrint = lazy(() => import(/* webpackChunkName: "PayrollPrint" */ "./Payroll/PayrollPrint"));
const PDF = lazy(() => import(/* webpackChunkName: "PDF" */ "./PDFs/PDF"));
const PDFHome = lazy(() => import(/* webpackChunkName: "PDFHome" */ "./PDFs/PDFHome"));
const PDFNew = lazy(() => import(/* webpackChunkName: "PDFNew" */ "./PDFs/PDFNew"));
const Permits = lazy(() => import(/* webpackChunkName: "Permits" */ "./Permits/Permits"));
const PermitsHome = lazy(() => import(/* webpackChunkName: "PermitsHome" */ "./Permits/PermitsHome"));
const PermitsNew = lazy(() => import(/* webpackChunkName: "PermitsNew" */ "./Permits/PermitsNew"));
const Photos = lazy(() => import(/* webpackChunkName: "Photos" */ "./Reporting/PhotosReport/Photos"));
const Product = lazy(() => import(/* webpackChunkName: "Product" */ "./ProductInventory/Product"));
const ProductHome = lazy(() => import(/* webpackChunkName: "ProductHome" */ "./ProductInventory/ProductHome"));
const ProductMetaTags = lazy(() => import(/* webpackChunkName: "ProductMetaTags" */ "./ProductInventory/ProductMetaTags"));
const QuotesHome = lazy(() => import(/* webpackChunkName: "QuotesHome" */ "./Quotes/QuotesHome"));
const Rates = lazy(() => import(/* webpackChunkName: "Rates" */ "./Rates/Rates"));
const RatesHome = lazy(() => import(/* webpackChunkName: "RatesHome" */ "./Rates/RatesHome"));
const RatesPrint = lazy(() => import(/* webpackChunkName: "RatesPrint" */ "./Rates/RatesPrint"));
const Reporting = lazy(() => import(/* webpackChunkName: "Reporting" */ "../containers/Reporting/Reporting"));
const RestrictedRoute = lazy(() => import(/* webpackChunkName: "RestrictedRoute" */ "./RestrictedRoute/RestrictedRoute"));
const Resume = lazy(() => import(/* webpackChunkName: "Resume" */ "./Resume/Resume"));
const ResumeHome = lazy(() => import(/* webpackChunkName: "ResumeHome" */ "./Resume/ResumeHome"));
const Role = lazy(() => import(/* webpackChunkName: "Role" */ "./Users/Roles/Role"));
const RolesHome = lazy(() => import(/* webpackChunkName: "RolesHome" */ "./Users/Roles/RolesHome"));
const Safety = lazy(() => import(/* webpackChunkName: "Safety" */ "./Safety/Safety"));
const SafetyHome = lazy(() => import(/* webpackChunkName: "SafetyHome" */ "./Safety/SafetyHome"));
const SafetyNew = lazy(() => import(/* webpackChunkName: "SafetyNew" */ "./Safety/SafetyNew"));
const SafetyReport = lazy(() => import(/* webpackChunkName: "SafetyReport" */ "./Safety/SafetyReport"));
const SalesEquipmentHome = lazy(() => import(/* webpackChunkName: "SalesEquipmentHome" */ "./EquipmentInventory/SalesEquipment/SalesEquipmentHome"));
const Scheduler = lazy(() => import(/* webpackChunkName: "Scheduler" */ "./Scheduler/Scheduler"));
const ServiceSettings = lazy(() => import(/* webpackChunkName: "ServiceSettings" */ "./Shop/ShopSettings/ServiceSettings"));
const ShopSettings = lazy(() => import(/* webpackChunkName: "ShopSettings" */ "./Shop/ShopSettings/ShopSettings"));
const StorageBox = lazy(() => import(/* webpackChunkName: "StorageBox" */ "./StorageBox/StorageBox"));
const Task = lazy(() => import(/* webpackChunkName: "Task" */ "./Reporting/TasksReport/Task"));
const TaskReportsHome = lazy(() => import(/* webpackChunkName: "TaskReportsHome" */ "./Users/Tasks/TaskReportsHome"));
const TasksEdit = lazy(() => import(/* webpackChunkName: "TasksEdit" */ "./Users/Tasks/TasksEdit"));
const TasksHome = lazy(() => import(/* webpackChunkName: "TasksHome" */ "./Users/Tasks/TasksHome"));
const TasksNew = lazy(() => import(/* webpackChunkName: "TasksNew" */ "./Users/Tasks/TasksNew"));
const TechHome = lazy(() => import(/* webpackChunkName: "TechHome" */ "./Tech/TechHome"));
const Ticket = lazy(() => import(/* webpackChunkName: "Ticket" */ "./Ticket/Ticket"));
const TicketsApproval = lazy(() => import(/* webpackChunkName: "TicketsApproval" */ "./TicketsApproval/TicketsApproval"));
const TicketTablePrintReport = lazy(() => import(/* webpackChunkName: "TicketTablePrintReport" */ "./Reporting/TicketReport/TicketTablePrintReport"));
const Timesheet = lazy(() => import(/* webpackChunkName: "Timesheet" */ "../containers/Reporting/TimesheetReport/Timesheet"));
const TimesheetPrint = lazy(() => import(/* webpackChunkName: "TimesheetPrint" */ "../containers/Reporting/TimesheetReport/TimesheetPrint"));
const TimesheetSelectedPrint = lazy(() => import(/* webpackChunkName: "TimesheetSelectedPrint" */ "./Reporting/TimesheetReport/TimesheetSelectedPrint"));
const TimesheetTablePayrollPrint = lazy(() => import(/* webpackChunkName: "TimesheetTablePayrollPrint" */ "../containers/Reporting/TimesheetReport/TimesheetTablePayrollPrint"));
const TimesheetTablePrint = lazy(() => import(/* webpackChunkName: "TimesheetTablePrint" */ "../containers/Reporting/TimesheetReport/TimesheetTablePrint"));
const TimesheetTotalPrint = lazy(() => import(/* webpackChunkName: "TimesheetTotalPrint" */ "./Reporting/TimesheetReport/TimesheetTotalPrint"));
const Unit = lazy(() => import(/* webpackChunkName: "Unit" */ "./Reporting/UnitReport/Unit"));
const UnitPrint = lazy(() => import(/* webpackChunkName: "UnitPrint" */ "./Reporting/UnitReport/UnitPrint"));
const User = lazy(() => import(/* webpackChunkName: "User" */ "./Users/User"));
const UserAsbtractAgreementPrint = lazy(() => import(/* webpackChunkName: "UserAsbtractAgreementPrint" */ "./Reporting/UserReport/UserAsbtractAgreementPrint"));
const UserAsbtractPermissionPrint = lazy(() => import(/* webpackChunkName: "UserAsbtractPermissionPrint" */ "./Reporting/UserReport/UserAbstractPermissionPrint"));
const UserHome = lazy(() => import(/* webpackChunkName: "UserHome" */ "./Users/UserHome"));
const UserNew = lazy(() => import(/* webpackChunkName: "UserNew" */ "./Users/UserNew"));
const UserPrint = lazy(() => import(/* webpackChunkName: "UserPrint" */ "./Reporting/UserReport/UserPrint"));
const UserReport = lazy(() => import(/* webpackChunkName: "UserReport" */ "./Users/UserReport"));
const UsersPrint = lazy(() => import(/* webpackChunkName: "UsersPrint" */ "./Users/UsersPrint"));
const WorkOrder = lazy(() => import(/* webpackChunkName: "WorkOrder" */ "./WorkOrders/WorkOrder"));
const WorkOrderNew = lazy(() => import(/* webpackChunkName: "WorkOrderNew" */ "./WorkOrders/WorkOrderNew"));
const WorkOrderPrint = lazy(() => import(/* webpackChunkName: "WorkOrderPrint" */ "./WorkOrders/WorkOrderPrint"));
const WorkOrdersHome = lazy(() => import(/* webpackChunkName: "WorkOrdersHome" */ "./WorkOrders/WorkOrdersHome"));
const ZoneSettings = lazy(() => import(/* webpackChunkName: "ZoneSettings" */ "./Shop/ShopSettings/ZoneSettings"));

//import DefectReports from "./Reporting/DefectReports/DefectReports";

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
    position: "relative",
  },
}));

const RouteSpinner = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: "15vh 0" }}>
    <CircularProgress />
  </div>
);

// roles is empty until the roles listener resolves; hold the route rather than
// redirecting a permitted user to /dashboard/restricted while it is pending.
const ProtectedRoute = (props) => {
  const { loading } = useAuth();
  if (loading) {
    return <Route {...props} render={() => <RouteSpinner />} />;
  }
  return <BaseProtectedRoute {...props} />;
};

const RoutesBoundary = (props) => {
  const location = useLocation();
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallBack}
      onError={(error, info) => {
        errorReporter(error, location, info, props.currentUser);
      }}
      resetKeys={[location.pathname]}
    >
      {props.children}
    </ErrorBoundary>
  );
};

// function Application() {
const Application = (props) => {
  const { currentUser, authLoading, roles } = useAuth();

  const classes = useStyles();

  const { subtypes, types } = useHRSettings();

  React.useEffect(() => {}, [props.colorMode]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    !authLoading && (
      <div className={classes.container}>
        {!_.isEmpty(currentUser) && currentUser !== null ? (
          <Drawer colorMode={props.colorMode} setColorMode={props.setColorMode}>
            <StylesProvider>
              <SalesEquipmentProvider>
                <CustomerProvider>
                  <ProductProvider>
                    <SettingsProvider>
                      <CouponProvider>
                        <UserProvider>
                          <FormProvider>
                            <UnitProvider>
                              <UnitDescriptionProvider>
                                <TimesheetTypeProvider>
                                  <Meta title={"Gee Tee Dashboard"} />
                                  <RoutesBoundary currentUser={currentUser}>
                                    <Suspense fallback={<RouteSpinner />}>
                                    <Switch>
                                      {/* Basic Routes*/}
                                      <Route path="/dashboard" exact>
                                        {" "}
                                        <Home />
                                      </Route>
                                      <Route path="/dashboard/restricted" exact>
                                        {" "}
                                        <RestrictedRoute />
                                      </Route>
                                      {/* HR Routes */}
                                      <Route path="/dashboard/hr/form" exact>
                                        {" "}
                                        <HR />
                                      </Route>
                                      {subtypes.map((subtype, index) => {
                                        return (
                                          <Route
                                            key={index}
                                            path={`/dashboard/hr/${subtype.parentValue}/${subtype.value}`}
                                            exact
                                          >
                                            {" "}
                                            <HRHome />
                                          </Route>
                                        );
                                      })}
                                      {types.map((type, index) => {
                                        return (
                                          <Route
                                            key={index}
                                            path={`/dashboard/hr/${type.value}/new`}
                                            exact
                                          >
                                            {" "}
                                            <HRNew />
                                          </Route>
                                        );
                                      })}
                                      {/* Customer Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/customers"
                                        exact
                                        componentArg={CustomerHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Customers"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/customers/form"
                                        exact
                                        componentArg={Customer}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Customers"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/customers/new"
                                        exact
                                        componentArg={CustomerNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Customers"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/customers/reporting"
                                        exact
                                        componentArg={CustomerReport}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Customers"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/customers/printing/table"
                                        exact
                                        componentArg={CustomerTablePrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Customers"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/customers/rates"
                                        exact
                                        componentArg={CustomerRates}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Customers"
                                        )}
                                      />

                                      <ProtectedRoute
                                        path="/dashboard/customers/reporting/printing"
                                        exact
                                        componentArg={CustomerPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Customers"
                                        )}
                                      />
                                      {/* Equipment Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/equipment/home"
                                        exact
                                        componentArg={EquipmentHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Equipment"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipment/new"
                                        exact
                                        componentArg={EquipmentNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Equipment"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipment/form"
                                        exact
                                        componentArg={Equipment}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Equipment"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipment/reporting"
                                        exact
                                        componentArg={EquipmentReport}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Equipment"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipment/printing"
                                        exact
                                        componentArg={EquipmentPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Equipment"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipment/defects"
                                        exact
                                        componentArg={DefectSettings}
                                        isAuth={roles.includes(
                                          "Manage Defect Settings"
                                        )}
                                      />
                                      {/* Employee Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/users/new"
                                        exact
                                        componentArg={UserNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/form"
                                        exact
                                        componentArg={User}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/memos"
                                        exact
                                        componentArg={EmployeeMemo}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/notifs"
                                        exact
                                        componentArg={EmployeeNotif}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users"
                                        exact
                                        componentArg={UserHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/roles"
                                        exact
                                        componentArg={RolesHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/roles/role"
                                        exact
                                        componentArg={Role}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/printing"
                                        exact
                                        componentArg={UsersPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employees"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/tasks"
                                        exact
                                        componentArg={TasksHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employee Tasks"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/tasks/task"
                                        exact
                                        componentArg={TasksEdit}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employee Tasks"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/tasks/new"
                                        exact
                                        componentArg={TasksNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employee Tasks"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/users/taskreports"
                                        exact
                                        componentArg={TaskReportsHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employee Tasks"
                                        )}
                                      />
                                      {/* Task.js limits users without the permission to their own tasks */}
                                      <ProtectedRoute
                                        path="/dashboard/users/taskreports/task"
                                        exact
                                        componentArg={Task}
                                        isAuth={true}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/reporting/tasks"
                                        exact
                                        componentArg={Task}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Employee Tasks"
                                        )}
                                      />
                                      {/* NOT PROTECTED BECAUSE OF ME PAGE */}
                                      <Route path="/dashboard/users/me" exact>
                                        {" "}
                                        <User />
                                      </Route>
                                      <Route
                                        path="/dashboard/users/reporting/printing"
                                        exact
                                      >
                                        {" "}
                                        <UserPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/users/reporting/printing/abstract"
                                        exact
                                      >
                                        {" "}
                                        <UserAsbtractAgreementPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/users/reporting/printing/abstractpermission"
                                        exact
                                      >
                                        {" "}
                                        <UserAsbtractPermissionPrint />
                                      </Route>
                                      {/* Reporting Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/reporting"
                                        exact
                                        componentArg={Reporting}
                                        colorMode={props.colorMode}
                                        setColorMode={props.setColorMode}
                                        isAuth={roles.includes(
                                          "Access Reports Section"
                                        )}
                                      />
                                      {/* NOT PROTECTED AS OF YET- SO MANY PERMISSIONS GIVE ACCESS, REPORTING, APPROVAL OF ANY TIMESHEET ECT */}
                                      <Route
                                        path="/dashboard/reporting/timesheets"
                                        exact
                                      >
                                        {" "}
                                        <Timesheet />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/checklist"
                                        exact
                                      >
                                        {" "}
                                        <Checklist />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/defect"
                                        exact
                                      >
                                        {" "}
                                        <Defect />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/delivery"
                                        exact
                                      >
                                        {" "}
                                        <Delivery />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/job"
                                        exact
                                      >
                                        {" "}
                                        <Job />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/printing"
                                        exact
                                      >
                                        {" "}
                                        <TimesheetPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/printing/table"
                                        exact
                                      >
                                        {" "}
                                        <TimesheetTablePrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/printing/payroll"
                                        exact
                                      >
                                        {" "}
                                        <TimesheetTablePayrollPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/printing/total"
                                        exact
                                      >
                                        {" "}
                                        <TimesheetTotalPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/printing/selected"
                                        exact
                                      >
                                        {" "}
                                        <TimesheetSelectedPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/photos"
                                        exact
                                      >
                                        {" "}
                                        <Photos />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/checklist/printing"
                                        exact
                                      >
                                        {" "}
                                        <ChecklistReportPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/jobs/printing/table"
                                        exact
                                      >
                                        {" "}
                                        <JobTablePrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/checklists/printing/table"
                                        exact
                                      >
                                        {" "}
                                        <ChecklistTablePrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/checklists/printing/all"
                                        exact
                                      >
                                        {" "}
                                        <ChecklistAllPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/orders/printing/selected"
                                        exact
                                      >
                                        {" "}
                                        <OrderSelectedPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/orders/printing"
                                        exact
                                      >
                                        {" "}
                                        <OrderPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/covidProtocol"
                                        exact
                                      >
                                        {" "}
                                        <Covid />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/timesheets/unit"
                                        exact
                                      >
                                        {" "}
                                        <Unit />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/documents"
                                        exact
                                      >
                                        {" "}
                                        <Document />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/units/printing"
                                        exact
                                      >
                                        {" "}
                                        <UnitPrint />
                                      </Route>
                                      <Route
                                        path="/dashboard/reporting/tickets/printing/report"
                                        exact
                                      >
                                        {" "}
                                        <TicketTablePrintReport />
                                      </Route>
                                      {/* Products/Cateogry/Coupons/Gift Card/Store Equipment Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/products"
                                        exact
                                        componentArg={ProductHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/products/metatags"
                                        exact
                                        componentArg={ProductMetaTags}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/products/product"
                                        exact
                                        componentArg={Product}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/products/categories"
                                        exact
                                        componentArg={CategoryHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/coupons"
                                        exact
                                        componentArg={CouponHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/coupons/coupon"
                                        exact
                                        componentArg={Coupon}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/giftCards/"
                                        exact
                                        componentArg={GiftCardsHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/giftCards/new"
                                        exact
                                        componentArg={GiftCardsNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/giftCards/form"
                                        exact
                                        componentArg={GiftCard}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/store/equipment"
                                        exact
                                        componentArg={SalesEquipmentHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Products"
                                        )}
                                      />
                                      {/* Forms/Checklist Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/checklists"
                                        exact
                                        componentArg={ChecklistHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/checklists/checklist"
                                        exact
                                        componentArg={Checklist2}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/checklists/printing"
                                        exact
                                        componentArg={ChecklistPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/pdfforms"
                                        exact
                                        componentArg={PDFHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/pdfforms/new"
                                        exact
                                        componentArg={PDFNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/pdfforms/form"
                                        exact
                                        componentArg={PDF}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/pdfforms/reporting"
                                        exact
                                        componentArg={SafetyReport}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/pdfforms/newdocreport"
                                        exact
                                        componentArg={NewDocReport}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Forms"
                                        )}
                                      />
                                      {/* Permits Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/equipment/permits"
                                        exact
                                        componentArg={PermitsHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Permits"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipment/permits/new"
                                        exact
                                        componentArg={PermitsNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Permits"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipment/permits/form"
                                        exact
                                        componentArg={Permits}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Permits"
                                        )}
                                      />
                                      {/* Settings Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/store/shopSettings"
                                        exact
                                        componentArg={ShopSettings}
                                        isAuth={roles.includes(
                                          "Edit Shop Settings"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/store/businessSettings"
                                        exact
                                        componentArg={BusinessSettings}
                                        isAuth={roles.includes(
                                          "Edit Business Settings"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/store/zoneSettings"
                                        exact
                                        componentArg={ZoneSettings}
                                        isAuth={roles.includes(
                                          "Edit Zone Settings"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/store/serviceSettings"
                                        exact
                                        componentArg={ServiceSettings}
                                        isAuth={roles.includes(
                                          "Edit Service Settings"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/store/careerSettings"
                                        exact
                                        componentArg={CareerSettings}
                                        isAuth={roles.includes(
                                          "Edit Career Settings"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/hr/settings"
                                        exact
                                        componentArg={HRSettings}
                                        isAuth={roles.includes(
                                          "Edit HR Folder Settings"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/hr/accessibility/settings"
                                        exact
                                        componentArg={AccessibilitySettings}
                                        isAuth={roles.includes(
                                          "Edit Accessibility Settings"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/settings/appSettings"
                                        exact
                                        componentArg={AppSettings}
                                        isAuth={roles.includes(
                                          "Update App Message"
                                        )}
                                      />
                                      {/* Store Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/store/orders"
                                        exact
                                        componentArg={Orders}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Orders"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/store/orders/newSquare"
                                        exact
                                        componentArg={OrderNewSquare}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Orders"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/store/orders/order"
                                        exact
                                        componentArg={Order}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Orders"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/quotes"
                                        exact
                                        componentArg={QuotesHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Orders"
                                        )}
                                      />
                                      {/* Workorder Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/workorders"
                                        exact
                                        componentArg={WorkOrdersHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Work Orders"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/workorders/new"
                                        exact
                                        componentArg={WorkOrderNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Work Orders"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/workorders/form"
                                        exact
                                        componentArg={WorkOrder}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Work Orders"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/workorders/printing"
                                        exact
                                        componentArg={WorkOrderPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Work Orders"
                                        )}
                                      />
                                      {/* Ticket/Invoice/Rates Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/tickets"
                                        exact
                                        componentArg={Ticket}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Tickets"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/invoicing"
                                        exact
                                        componentArg={InvoiceHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Invoices"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/invoicing/new"
                                        exact
                                        componentArg={InvoiceNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Invoices"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/invoicing/form"
                                        exact
                                        componentArg={Invoice}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Invoices"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/invoicing/printing"
                                        exact
                                        componentArg={InvoicePrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Invoices"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/estimates"
                                        exact
                                        componentArg={EstimatesHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Estimates"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/estimates/form"
                                        exact
                                        componentArg={Estimates}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Estimates"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/rates"
                                        exact
                                        componentArg={RatesHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Rates"
                                        )}
                                      />
                                     <ProtectedRoute
                                        path="/dashboard/rates/printing"
                                        exact
                                        componentArg={RatesPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Rates"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/rates/form"
                                        exact
                                        componentArg={Rates}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Rates"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/ticketapproval"
                                        exact
                                        componentArg={TicketsApproval}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Tickets"
                                        )}
                                      />
                                      {/* Grain Routes */}
                                      <ProtectedRoute
                                        path="/dashboard/grains"
                                        exact
                                        componentArg={GrainOrdersHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Grain Reports"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/grains/new"
                                        exact
                                        componentArg={GrainOrdersNew}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Grain Reports"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/grains/form"
                                        exact
                                        componentArg={GrainOrders}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Grain Reports"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/grains/printing"
                                        exact
                                        componentArg={GrainPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Grain Reports"
                                        )}
                                      />
                                      {/* Tech Routes HIDDEN BASED ON EMAILS LMAO */}
                                      <Route path="/dashboard/tech" exact>
                                        {" "}
                                        <TechHome />
                                      </Route>
                                      <Route
                                        path="/dashboard/changelog/logs"
                                        exact
                                      >
                                        {" "}
                                        <ChangelogHome />
                                      </Route>
                                      <Route
                                        path="/dashboard/tech/bugreports"
                                        exact
                                      >
                                        {" "}
                                        <BugReportsHome />
                                      </Route>
                                      <ProtectedRoute
                                        path="/dashboard/storage"
                                        exact
                                        componentArg={StorageBox}
                                        isAuth={roles.includes(
                                          "Use File Storage"
                                        )}
                                      />
                                      {/* Misc Routes */}
                                      <Route
                                        path="/dashboard/scheduler/view"
                                        exact
                                      >
                                        {" "}
                                        <Scheduler />
                                      </Route>{" "}
                                      {/* scheduler doesnt have a perm? */}
                                      <Route
                                        path="/dashboard/approval/timesheets"
                                        exact
                                      >
                                        {" "}
                                        <ApprovalHome />
                                      </Route>
                                      <ProtectedRoute
                                        path="/dashboard/store/conversations"
                                        exact
                                        componentArg={Conversations}
                                        isAuth={roles.includes(
                                          "Manage Message Center"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/resumes"
                                        exact
                                        componentArg={ResumeHome}
                                        isAuth={roles.includes(
                                          "Manage Resumes"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/resumes/resume"
                                        exact
                                        componentArg={Resume}
                                        isAuth={roles.includes(
                                          "Manage Resumes"
                                        )}
                                      />
                                      {/* Equipment Ifta */}
                                      <ProtectedRoute
                                        path="/dashboard/equipmentIfta"
                                        exact
                                        componentArg={EquipmentIftaHome}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Ifta"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipmentIfta/form"
                                        exact
                                        componentArg={EquipmentIfta}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Ifta"
                                        )}
                                      />
                                      <ProtectedRoute
                                        path="/dashboard/equipmentIfta/printing"
                                        exact
                                        componentArg={EquipmentIftaPrint}
                                        isAuth={roles.includes(
                                          "Create|Update|Delete Ifta"
                                        )}
                                      />
                                      {/* Unused Routes */}
                                      <Route path="/dashboard/forms" exact>
                                        {" "}
                                        <FormHome />
                                      </Route>
                                      <Route path="/dashboard/forms/form" exact>
                                        {" "}
                                        <Form />
                                      </Route>
                                      <Route path="/dashboard/forms/new" exact>
                                        {" "}
                                        <FormNew />
                                      </Route>
                                      <Route
                                        path="/dashboard/forms/reporting"
                                        exact
                                      >
                                        {" "}
                                        <FormReport />
                                      </Route>
                                      <Route
                                        path="/dashboard/users/reporting"
                                        exact
                                      >
                                        {" "}
                                        <UserReport />
                                      </Route>
                                      <Route
                                        path="/dashboard/store/orders/new"
                                        exact
                                      >
                                        {" "}
                                        <OrderNew />
                                      </Route>
                                      <Route path="/dashboard/payroll" exact>
                                        {" "}
                                        <Payroll />
                                      </Route>
                                      <Route
                                        path="/dashboard/payroll/printing"
                                        exact
                                      >
                                        {" "}
                                        <PayrollPrint />
                                      </Route>
                                      <Route path="/dashboard/safety" exact>
                                        {" "}
                                        <SafetyHome />
                                      </Route>
                                      <Route path="/dashboard/safety/new" exact>
                                        {" "}
                                        <SafetyNew />
                                      </Route>
                                      <Route
                                        path="/dashboard/safety/form"
                                        exact
                                      >
                                        {" "}
                                        <Safety />
                                      </Route>
                                      <Route
                                        path="/dashboard/safety/reporting"
                                        exact
                                      >
                                        {" "}
                                        <SafetyReport />
                                      </Route>
                                      <Route path="/dashboard/msds" exact>
                                        {" "}
                                        <MSDSHome />
                                      </Route>
                                      <Route path="/dashboard/msds/new" exact>
                                        {" "}
                                        <MSDSNew />
                                      </Route>
                                      <Route path="/dashboard/msds/form" exact>
                                        {" "}
                                        <MSDS />
                                      </Route>
                                    </Switch>
                                    </Suspense>
                                  </RoutesBoundary>
                                </TimesheetTypeProvider>
                              </UnitDescriptionProvider>
                            </UnitProvider>
                          </FormProvider>
                        </UserProvider>
                      </CouponProvider>
                    </SettingsProvider>
                  </ProductProvider>
                </CustomerProvider>
              </SalesEquipmentProvider>
            </StylesProvider>
          </Drawer>
        ) : (
          <SignIn />
        )}
      </div>
    )
  );
};

export default React.memo(Application);
